import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  fetchSavedViews,
  deleteSavedView,
  duplicateSavedView,
  togglePinSavedView,
  setDefaultSavedView,
} from "../../../../api/t2tSavedViewsApi";
import SaveViewModal  from "./SaveViewModal";
import ShareViewModal from "./ShareViewModal";

export default function SavedViewsDropdown({ activeViewId, onApply, currentConfig }) {
  const [open, setOpen]           = useState(false);
  const [views, setViews]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [menuViewId, setMenuViewId] = useState(null); // 3-dot context menu
  const [showSave, setShowSave]   = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [shareTarget, setShareTarget] = useState(null);
  const ref = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchSavedViews();
      setViews(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (_) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const activeView = views.find(v => v.id === activeViewId);

  const handleApply = (view) => {
    onApply(view);
    setOpen(false);
  };

  const handlePin = async (viewId, e) => {
    e.stopPropagation();
    try {
      await togglePinSavedView(viewId);
      load();
    } catch (_) { /* silent */ }
  };

  const handleSetDefault = async (viewId, e) => {
    e.stopPropagation();
    try {
      await setDefaultSavedView(viewId);
      load();
    } catch (_) { /* silent */ }
  };

  const handleDuplicate = async (viewId, e) => {
    e.stopPropagation();
    setMenuViewId(null);
    try {
      await duplicateSavedView(viewId);
      load();
    } catch (_) { /* silent */ }
  };

  const handleDelete = async (viewId, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this saved view?")) return;
    setMenuViewId(null);
    try {
      await deleteSavedView(viewId);
      load();
    } catch (_) { /* silent */ }
  };

  const pinned = views.filter(v => v.is_pinned);
  const mine   = views.filter(v => !v.is_pinned && v.access_level === "owner");
  const shared = views.filter(v => !v.is_pinned && v.access_level !== "owner");

  return (
    <div ref={ref} className="position-relative d-inline-flex align-items-center gap-1">
      {/* Trigger button */}
      <button
        className="t2t-btn t2t-btn-ghost t2t-btn-sm d-flex align-items-center gap-1"
        onClick={() => setOpen(o => !o)}
        title="Saved views"
      >
        <i className="bi bi-bookmark" style={{ fontSize: 11 }} />
        <span>{activeView ? activeView.name : "Views"}</span>
        <i className="bi bi-chevron-down" style={{ fontSize: 9 }} />
      </button>

      {/* Save current view button */}
      <button
        className="t2t-btn t2t-btn-ghost t2t-btn-sm"
        title="Save current view"
        onClick={() => { setEditTarget(null); setShowSave(true); }}
      >
        <i className="bi bi-plus" style={{ fontSize: 13 }} />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, width: 280, zIndex: 1030, fontSize: 13, background: "var(--t2t-surface)", border: "1px solid var(--t2t-border)", borderRadius: "var(--t2t-radius)", boxShadow: "var(--t2t-shadow-md, 0 4px 20px rgba(0,0,0,0.12))" }}
        >
          {loading && <p className="text-muted text-center py-3 mb-0">Loading…</p>}

          {!loading && views.length === 0 && (
            <p className="text-muted text-center py-3 mb-0 small">No saved views yet.</p>
          )}

          {!loading && [
            { title: "Pinned", items: pinned },
            { title: "Mine",   items: mine   },
            { title: "Shared", items: shared },
          ].map(({ title, items }) => items.length === 0 ? null : (
            <div key={title}>
              <div className="px-3 py-1 text-muted" style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {title}
              </div>
              {items.map(view => (
                <div
                  key={view.id}
                  className={`d-flex align-items-center px-3 py-2 cursor-pointer ${activeViewId === view.id ? "bg-primary bg-opacity-10" : "hover-bg-light"}`}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleApply(view)}
                >
                  <span className="flex-grow-1 text-truncate" title={view.name}>{view.name}</span>

                  {/* Pin toggle */}
                  <button
                    className="btn btn-link p-0 ms-1 text-muted"
                    style={{ fontSize: 12 }}
                    title={view.is_pinned ? "Unpin" : "Pin"}
                    onClick={e => handlePin(view.id, e)}
                  >
                    <i className={`bi ${view.is_pinned ? "bi-pin-fill text-primary" : "bi-pin"}`} />
                  </button>

                  {/* Default toggle */}
                  <button
                    className="btn btn-link p-0 ms-1 text-muted"
                    style={{ fontSize: 12 }}
                    title={view.is_default ? "Default view" : "Set as default"}
                    onClick={e => handleSetDefault(view.id, e)}
                  >
                    <i className={`bi ${view.is_default ? "bi-star-fill text-warning" : "bi-star"}`} />
                  </button>

                  {/* Context menu trigger (owner only) */}
                  {view.access_level === "owner" && (
                    <div className="position-relative">
                      <button
                        className="btn btn-link p-0 ms-1 text-muted"
                        style={{ fontSize: 12 }}
                        onClick={e => { e.stopPropagation(); setMenuViewId(menuViewId === view.id ? null : view.id); }}
                      >
                        <i className="bi bi-three-dots" />
                      </button>
                      {menuViewId === view.id && (
                        <div
                          className="shadow border rounded bg-white"
                          style={{ position: "absolute", right: 0, top: "100%", width: 150, zIndex: 1040, fontSize: 13 }}
                          onClick={e => e.stopPropagation()}
                        >
                          <button className="dropdown-item py-2" onClick={() => { setEditTarget(view); setShowSave(true); setMenuViewId(null); }}>
                            <i className="bi bi-pencil me-2" />Edit
                          </button>
                          <button className="dropdown-item py-2" onClick={(e) => handleDuplicate(view.id, e)}>
                            <i className="bi bi-copy me-2" />Duplicate
                          </button>
                          <button className="dropdown-item py-2" onClick={() => { setShareTarget(view); setShowShare(true); setMenuViewId(null); }}>
                            <i className="bi bi-share me-2" />Share
                          </button>
                          <div className="dropdown-divider my-0" />
                          <button className="dropdown-item py-2 text-danger" onClick={(e) => handleDelete(view.id, e)}>
                            <i className="bi bi-trash me-2" />Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <SaveViewModal
        show={showSave}
        onClose={() => { setShowSave(false); setEditTarget(null); }}
        onSaved={() => { load(); setOpen(true); }}
        existingView={editTarget}
        currentConfig={currentConfig}
      />
      <ShareViewModal
        show={showShare}
        onClose={() => { setShowShare(false); setShareTarget(null); }}
        view={shareTarget}
        onUpdated={() => load()}
      />
    </div>
  );
}

SavedViewsDropdown.propTypes = {
  activeViewId:  PropTypes.number,
  onApply:       PropTypes.func.isRequired,
  currentConfig: PropTypes.object,
};
